import {Component, computed, effect, inject, OnDestroy, OnInit, Signal, signal,} from "@angular/core";
import {CommonModule} from "@angular/common";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatSelectModule} from "@angular/material/select";
import {UserDataService} from "src/app/features/admin/data-access/user-data.service";
import {StorageService} from "src/app/shared/data-access/storage.service";
import {ProductionLineService} from "src/app/features/admin/data-access/production-line.service";
import {SaveCancelButtonsComponent} from "../../../components/save-cancel-buttons/save-cancel-buttons.component";
import {NavigationService} from "src/app/shared/data-access/navigation.service";
import {ActivatedRoute, Router} from "@angular/router";
import {User, Userrole} from "src/app/shared/interfaces/user";
import {ApiService} from "src/app/services/api.service";
import {
  ResetPasswordDialogComponent
} from "src/app/features/admin/components/reset-password-dialog/reset-password-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {environment} from "src/environments/environment";
import {startWith, Subscription, tap} from "rxjs";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";

type FormAction = "create" | "edit" | "";

@Component({
  selector: "app-user-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    SaveCancelButtonsComponent,
    MatSlideToggleModule
  ],
  templateUrl: "./user-form.component.html",
  styleUrls: ["./user-form.component.scss"],
})
export class UserFormComponent implements OnInit, OnDestroy {
  Userrole = Userrole;
  userDataService = inject(UserDataService);
  storageService = inject(StorageService);
  apiService = inject(ApiService);
  productionLineService = inject(ProductionLineService);
  navigationService = inject(NavigationService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  formBuilder = inject(FormBuilder);
  readonly dialog = inject(MatDialog);
  action = signal<FormAction>("");

  private emailValidators: Subscription;
  private productionLineValidators: Subscription;
  private passwordValidators: Subscription


  userForm: FormGroup = this.formBuilder.group({
    username: ["", Validators.required],
    firstName: ["", Validators.required],
    lastName: ["", Validators.required],
    isActive: ["", Validators.required],
    userrole: ["", Validators.required],
    productionLine: [""],
    email: ["", Validators.email],
    phone: [""],
    password: [""],
    notifications: this.formBuilder.array([])
  });

  protected userBeingEdited = computed(() => {
    const url = this.navigationService.currentUrl();
    const userUuid = url.split("user/")[1]?.split("?")[0];
    if (userUuid) {
      return (
        this.userDataService.users().find((user) => user.id === userUuid) ||
        null
      );
    } else {
      return null;
    }
  });

  constructor() {
    this.action.set(
      this.route.snapshot.queryParams["formAction"] as FormAction
    );
  }

  get notificationsFormArray(): FormArray {
    return this.userForm.get('notifications') as FormArray;
  }

  initializeNotifications() {
    while (this.notificationsFormArray.length) {
      this.notificationsFormArray.removeAt(0);
    }
    this.productionLineService.productionLines().forEach(line => {
      const isEnabled = this.userBeingEdited()?.notifications?.includes(line.id!) || false;
      this.notificationsFormArray.push(this.formBuilder.control(isEnabled));
    })
  }

  ngOnInit() {
    this.getUserUuidFromUrl();
    const user: User | null = this.userBeingEdited();
    if (!user) {
      this.userForm.reset();
    } else {
      this.userForm.patchValue({
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        isActive: user.isActive,
        userrole: user.userrole,
        productionLine: user.productionLine,
        email: user.email,
        phone: user.phone,
      });

      if (user.userrole === Userrole.Techniker) {
        this.initializeNotifications()
      }
    }

    this.emailValidators = this.setupConditionalValidator(
      'email',
      'userrole',
      (userrole: Userrole) => this.isEmailRequired(userrole)
    )
    this.productionLineValidators = this.setupConditionalValidator(
      'productionLine',
      'userrole',
      (userrole: Userrole) => this.isProductionLineRequired(userrole)
    )
    this.passwordValidators = this.setupConditionalValidator(
      'password',
      'password',
      () => this.action() === 'create'
    )
    this.userForm.get('userrole')!.valueChanges.subscribe((role) => {
      if (role === Userrole.Techniker) {
        this.initializeNotifications()
      }
    })
    if (this.userBeingEdited()?.userrole === Userrole.Techniker) {
      this.initializeNotifications()
    }
  }

  ngOnDestroy(): void {
    this.emailValidators.unsubscribe();
    this.productionLineValidators.unsubscribe();
    this.passwordValidators.unsubscribe()
  }

  getUserUuidFromUrl(): Signal<User | null> {
    return this.userBeingEdited;
  }

  resetPassword(): void {
    const dialogRef = this.dialog.open(ResetPasswordDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        this.apiService.initializePasswordReset(
          this.userBeingEdited()!.id!,
          result.newPassword
        );
      }
    });
  }

  save() {
    let notificationData: string[] = [];
    if (this.userForm.get('userrole')!.value === Userrole.Techniker) {
      notificationData = this.notificationsFormArray.controls
        .map((control, index) => {
          return {
            selected: control.value,
            lineId: this.productionLineService.productionLines()[index].id
          };
        })
        .filter(item => item.selected)
        .map(item => item.lineId!);
    }

    if (this.userForm.valid) {
      if (this.action() === "create" && this.userForm.valid) {
        this.userDataService.add$.next({
          authEmail:
            this.userForm.get("username")!.value + environment.authEmail,
          requirePasswordUpdate: true,
          ...this.userForm.value,
          notifications: notificationData
        });
        alert("Benutzer hinzugefügt");
      } else if (this.action() === "edit" && this.userForm.valid) {
        this.userDataService.update$.next({
          id: this.userBeingEdited()?.id,
          authEmail: this.userForm.get("username")!.value.includes("@")
            ? this.userForm.get("username")!.value
            : this.userForm.get("username")!.value + environment.authEmail,
          ...this.userForm.value,
          notifications: notificationData
        });
        // console.log(this.userForm.value);
        alert("Änderungen gespeichert");
      }
      this.router.navigate(["/admin/users"]);
    } else {
      alert("Bitte alle Pflichtfelder ausfüllen");
    }
  }

  private setupConditionalValidator(
    controlName: string,
    dependentControl: string,
    condition: (value: any) => boolean
  ) {
    return this.userForm.controls[dependentControl].valueChanges.pipe(
      tap(() => this.userForm.controls[controlName].markAsDirty()),
      startWith(this.userForm.controls[dependentControl].value)
    ).subscribe(value => {
      const control = this.userForm.controls[controlName];
      condition(value)
        ? control.addValidators(Validators.required)
        : control.removeValidators(Validators.required);
      control.updateValueAndValidity();
    });
  }

  private isEmailRequired(userrole: Userrole): boolean {
    return !(userrole === Userrole.Schichtleiter || userrole === Userrole.Mitarbeiter);
  }

  private isProductionLineRequired(userrole: Userrole): boolean {
    return userrole === Userrole.Teamleiter || userrole === Userrole.Schichtleiter
  }
}
